import { useMemo, useRef, useState } from "react";
import type { ColumnType } from "antd/es/table";
import type { RequirementDetailType } from "./useGetDetail";

type DetailColumn = ColumnType<RequirementDetailType>;

// 只在本次详情展示期间保存列顺序，选择列始终留在最左侧。
export default function useColumnDrag(columns: DetailColumn[]) {
  const [columnOrder, setColumnOrder] = useState<React.Key[]>([]);
  const [unlockedKeys, setUnlockedKeys] = useState<React.Key[]>([]);
  const draggingKey = useRef<React.Key | null>(null);

  return useMemo(() => {
    const visibleColumns = columns.filter(column => !column.hidden);
    const columnMap = new Map(visibleColumns.map(column => [column.key, column]));
    const orderedColumns = [
      ...columnOrder.map(key => columnMap.get(key)).filter((column): column is DetailColumn => !!column),
      ...visibleColumns.filter(column => !columnOrder.includes(column.key!)),
    ].map(column => ({
      ...column,
      fixed: unlockedKeys.includes(column.key!) ? undefined : column.fixed,
    }));

    // 与原表一致，仅连续的左右边缘列保持固定。
    const leftEnd = orderedColumns.findIndex(column => column.fixed !== "left");
    let rightStart = orderedColumns.length;
    while (rightStart > 0 && orderedColumns[rightStart - 1].fixed === "right") rightStart -= 1;

    return orderedColumns.map((column, index): DetailColumn => {
      const fixed = (leftEnd < 0 || index < leftEnd) ? "left" : index >= rightStart ? "right" : undefined;
      if (column.key === "selection") return { ...column, fixed };

      return {
        ...column,
        fixed,
        onHeaderCell: () => ({
          draggable: false,
          onMouseDown: event => {
            // 列宽手柄和筛选/排序按钮仍使用原本的鼠标交互。
            const target = event.target as HTMLElement;
            event.currentTarget.draggable = !target.closest(".react-resizable-handle, button, input");
          },
          onMouseUp: event => { event.currentTarget.draggable = false; },
          onDragStart: event => {
            draggingKey.current = column.key!;
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", String(column.key));
          },
          onDragOver: event => {
            if (draggingKey.current === null) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
          },
          onDrop: event => {
            event.preventDefault();
            const sourceKey = draggingKey.current;
            draggingKey.current = null;
            const from = orderedColumns.findIndex(item => item.key === sourceKey);
            if (from < 0 || from === index) return;

            const nextColumns = [...orderedColumns];
            const [movedColumn] = nextColumns.splice(from, 1);
            nextColumns.splice(index, 0, movedColumn);
            // 固定列移出原来的固定区域后，本次打开期间解除固定。
            const remainsFixed = movedColumn.fixed === "left"
              ? nextColumns.slice(0, index + 1).every(item => item.fixed === "left")
              : movedColumn.fixed === "right"
                ? nextColumns.slice(index).every(item => item.fixed === "right")
                : false;
            if (movedColumn.fixed && !remainsFixed) {
              setUnlockedKeys(keys => [...keys, movedColumn.key!]);
            }
            setColumnOrder(nextColumns.map(item => item.key!));
            event.currentTarget.draggable = false;
          },
          onDragEnd: event => {
            draggingKey.current = null;
            event.currentTarget.draggable = false;
          },
        }),
      };
    });
  }, [columns, columnOrder, unlockedKeys]);
}
